// src/components/ProductList.tsx

import React, { useState, useEffect } from "react";
import "./ProductList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSlidersH } from "@fortawesome/free-solid-svg-icons";
import FilterIcon from "../../assets/icons/Filters lines.svg";
import { useNavigate, useSearchParams } from "react-router-dom";

// Import shared products data
import { products, Product } from "../../data/products";

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [filters, setFilters] = useState<{
    voltage: string[];
    power: string[];
    size: string[];
    lumen: string;
    base: string[];
    lifeHours: string[];
    beamAngle: string;
    categories: string[];
  }>(() => {
    const urlCategories = searchParams.get("categories");
    const initialCategories = urlCategories
      ? urlCategories
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      : [];

    return {
      voltage: [],
      power: [],
      size: [],
      lumen: "",
      base: [],
      lifeHours: [],
      beamAngle: "",
      categories: initialCategories,
    };
  });

  const urlCategories = searchParams.get("categories");
  const initialCategories = urlCategories
    ? urlCategories
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean)
    : [];

  const [sliderValue, setSliderValue] = useState<number>(0);
  const [sliderMax, setSliderMax] = useState<number>(360);
  const [sliderKnobPosition, setSliderKnobPosition] = useState<number>(0);

  const allBeamAngles = React.useMemo(() => {
    const angles = new Set<string>();
    products.forEach((product) => {
      if (product.beamAngle) {
        const angleStr = product.beamAngle;
        const numbers = angleStr.match(/\d+/g);
        if (numbers) {
          numbers.forEach((num) => angles.add(num));
        }
      }
    });
    return Array.from(angles)
      .map((angle) => parseInt(angle))
      .sort((a, b) => a - b);
  }, []);

  useEffect(() => {
    if (allBeamAngles.length > 0) {
      const maxAngle = Math.max(...allBeamAngles);
      setSliderMax(maxAngle);
    }
  }, [allBeamAngles]);

  useEffect(() => {
    if (filters.beamAngle) {
      const angleValue = parseInt(filters.beamAngle.replace("°", ""));
      if (!isNaN(angleValue)) {
        const position = (angleValue / sliderMax) * 100;
        setSliderKnobPosition(Math.min(Math.max(position, 0), 100));
        setSliderValue(angleValue);
      }
    } else {
      setSliderKnobPosition(0);
      setSliderValue(0);
    }
  }, [filters.beamAngle, sliderMax]);

  const handleFilterChange = (
    category: keyof typeof filters,
    value: string
  ) => {
    setFilters((prev) => {
      if (category === "lumen" || category === "beamAngle") {
        return { ...prev, [category]: value };
      }

      const current = prev[category] as string[];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      return { ...prev, [category]: updated };
    });
  };

  const clearFilters = () => {
    setFilters({
      voltage: [],
      power: [],
      size: [],
      lumen: "",
      base: [],
      lifeHours: [],
      beamAngle: "",
      categories: [],
    });
    setSliderValue(0);
    setSliderKnobPosition(0);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setSliderValue(value);
    setSliderKnobPosition((value / sliderMax) * 100);
    setFilters((prev) => ({ ...prev, beamAngle: `${value}°` }));
  };

  const handleBeamAngleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;
    let cleanedValue = value.replace(/[^0-9°]/g, "");
    const numbers = cleanedValue.match(/\d+/g);
    const hasDegree = cleanedValue.includes("°");

    if (numbers && numbers.length > 0) {
      const numericPart = numbers.join("");
      const finalValue = `${numericPart}°`;
      if (finalValue !== filters.beamAngle) {
        handleFilterChange("beamAngle", finalValue);
      }
    } else if (hasDegree) {
      handleFilterChange("beamAngle", "°");
    } else {
      handleFilterChange("beamAngle", "");
    }
  };

  const normalizeSize = (size: string): string => {
    if (!size) return "";
    return size
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/x/gi, "x")
      .replace(/\(mm\)/gi, "")
      .replace(/\(cm\)/gi, "");
  };

  const baseMatches = (productBase: string, filterBases: string[]): boolean => {
    if (!productBase) return false;
    const productBases = productBase.split("/").map((b) => b.trim());
    return filterBases.some((filterBase) =>
      productBases.some(
        (prodBase) =>
          prodBase === filterBase ||
          filterBase.includes(prodBase) ||
          prodBase.includes(filterBase)
      )
    );
  };

  const extractPower = (power: string): string => {
    if (!power) return "";
    const match = power.match(/^(\d+\.?\d*W)/);
    return match ? match[1] : power;
  };

  const beamAngleMatches = (
    productBeamAngle: string | undefined,
    filterBeamAngle: string
  ): boolean => {
    if (!filterBeamAngle || filterBeamAngle === "") return true;
    if (!productBeamAngle) return false;

    const filterAngle = parseInt(filterBeamAngle.replace("°", ""));
    if (isNaN(filterAngle)) return false;

    const productAngles = productBeamAngle
      .split("-")
      .map((angle) => {
        const num = parseInt(angle.replace("°", "").trim());
        return isNaN(num) ? null : num;
      })
      .filter((angle) => angle !== null) as number[];

    if (productAngles.length === 2) {
      return filterAngle >= productAngles[0] && filterAngle <= productAngles[1];
    }
    if (productAngles.length === 1) {
      return productAngles[0] === filterAngle;
    }
    return false;
  };

  const filteredProducts = products.filter((product) => {
    const matchesCategory =
      filters.categories.length === 0 ||
      (product.categories || []).some((cat) =>
        filters.categories.includes(cat)
      );

    if (!matchesCategory) return false;

    const matchesBase =
      filters.base.length === 0 ||
      (product.base && baseMatches(product.base, filters.base));

    const matchesLifeHours =
      filters.lifeHours.length === 0 ||
      (product.lifeHours &&
        filters.lifeHours.some((filterLife) =>
          product.lifeHours?.includes(filterLife)
        ));

    const matchesBeamAngle = beamAngleMatches(
      product.beamAngle,
      filters.beamAngle
    );

    if (!matchesBase || !matchesLifeHours || !matchesBeamAngle) {
      return false;
    }

    const hasMatchingVariant = product.variants.some((variant) => {
      const matchesVoltage =
        filters.voltage.length === 0 ||
        filters.voltage.includes(variant.voltage);

      const matchesPower =
        filters.power.length === 0 ||
        filters.power.some((filterPower) => {
          const variantPower = extractPower(variant.power);
          return variantPower === filterPower || variant.power === filterPower;
        });

      const matchesSize =
        filters.size.length === 0 ||
        filters.size.some((filterSize) => {
          const normalizedFilterSize = normalizeSize(filterSize);
          const normalizedVariantSize = normalizeSize(variant.size);

          if (filterSize.includes("mm")) {
            return variant.size === filterSize.replace(" (mm)", "");
          }
          if (filterSize.includes("cm")) {
            return normalizedFilterSize === normalizedVariantSize;
          }

          return (
            variant.size === filterSize ||
            normalizedVariantSize === normalizedFilterSize
          );
        });

      const matchesLumen =
        filters.lumen === "" || variant.lumen === filters.lumen;

      return matchesVoltage && matchesPower && matchesSize && matchesLumen;
    });

    return hasMatchingVariant;
  });

  const activeBarWidth = `${sliderKnobPosition}%`;

  // Count active filters for badge
  const activeFilterCount =
    filters.voltage.length +
    filters.power.length +
    filters.size.length +
    filters.base.length +
    filters.lifeHours.length +
    filters.categories.length +
    (filters.lumen ? 1 : 0) +
    (filters.beamAngle ? 1 : 0);

  // Shared filter panel content
  const FilterPanel = () => (
    <>
      {/* Clear Filter */}
      <div className="frame-3 clear-filter" onClick={clearFilters}>
        <span>Clear Filter</span>
        <div className="frame-5">
          <FontAwesomeIcon icon={faTimes} className="close-icon" />
        </div>
      </div>

      {/* Categories */}
      <div className="categories-filter">
        <h6 className="voltage">Categories</h6>
        <div className="column">
          {[
            "Lamps",
            "Home Lighting",
            "Commercial Lighting",
            "Industrial Lighting",
            "Street Lighting",
            "Security Lighting",
          ].map((cat) => (
            <label key={cat} className="checkbox">
              <input
                type="checkbox"
                checked={filters.categories.includes(cat)}
                onChange={() => handleFilterChange("categories", cat)}
              />
              <span>{cat}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Power */}
      <div className="frame-53">
        <h6 className="voltage">Power</h6>
        <div className="frame-59">
          <div className="column">
            {[
              "1W",
              "3W",
              "4W",
              "4.8W",
              "5W",
              "6W",
              "7W",
              "8W",
              "9W",
              "10W",
              "12W",
              "14W",
              "150W",
            ].map((value) => (
              <label key={value} className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.power.includes(value)}
                  onChange={() => handleFilterChange("power", value)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
          <div className="column">
            {[
              "15W",
              "18W",
              "20W",
              "22W",
              "28W",
              "30W",
              "38W",
              "48W",
              "50W",
              "54W",
              "72W",
              "100W",
              "200W",
            ].map((value) => (
              <label key={value} className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.power.includes(value)}
                  onChange={() => handleFilterChange("power", value)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Base */}
      <div className="frame-54">
        <h6 className="voltage">Base</h6>
        <div className="frame-59">
          <div className="column">
            {["B22", "E27", "E40", "G5"].map((value) => (
              <label key={value} className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.base.includes(value)}
                  onChange={() => handleFilterChange("base", value)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
          <div className="column">
            {["G13", "GU5.3 / MR16", "GU10", "E14"].map((value) => (
              <label key={value} className="checkbox">
                <input
                  type="checkbox"
                  checked={filters.base.includes(value)}
                  onChange={() => handleFilterChange("base", value)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="frame-46">
      <div className="frame-47">
        <h2 className="discover-products">
          <span className="discover-gray">Discover</span>{" "}
          <span className="products-black">Products</span>
        </h2>

        <div className="frame-3">
          <span className="popular-products">Popular Products</span>
          <div className="frame-4">
            <span className="icon">
              <img src={FilterIcon} alt="Filters" />
            </span>
          </div>
        </div>
      </div>

      <div className="frame-42">
        {/* Left sidebar - desktop only */}
        <div className="frame-48">
          <FilterPanel />
        </div>

        {/* Right column - Hero image + Filter button (mobile) + Product grid */}
        <div className="products-column">
          <div className="hero-image-container">
            <img
              src="/Frame109.jpg.webp"
              alt="Lighting products workshop"
              className="product-hero-image"
            />
          </div>

          {/* Mobile filter button - only visible on mobile */}
          <div className="mobile-filter-bar">
            <button
              className="mobile-filter-btn"
              onClick={() => setMobileFiltersOpen(true)}
            >
              <FontAwesomeIcon icon={faSlidersH} />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span className="mobile-filter-badge">{activeFilterCount}</span>
              )}
            </button>
            <span className="mobile-product-count">
              {filteredProducts.length} product
              {filteredProducts.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="frame-45">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="product-card"
                  onClick={() => navigate(`/product-detail/${product.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    navigate(`/product-detail/${product.id}`)
                  }
                >
                  <div className="img">
                    <img loading="lazy" decoding="async" src={product.image} alt={product.name} />
                  </div>
                  <div className="frame-44">
                    <span>{product.name}</span>
                  </div>
                </div>
              ))
            ) : (
              <p>No products match the selected filters.</p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer overlay */}
      {mobileFiltersOpen && (
        <div
          className="mobile-filter-overlay"
          onClick={() => setMobileFiltersOpen(false)}
        />
      )}

      {/* Mobile filter drawer */}
      <div
        className={`mobile-filter-drawer ${mobileFiltersOpen ? "open" : ""}`}
      >
        <div className="mobile-filter-drawer-header">
          <span>Filters</span>
          <button
            className="mobile-filter-drawer-close"
            onClick={() => setMobileFiltersOpen(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="mobile-filter-drawer-content">
          <FilterPanel />
        </div>
        <div className="mobile-filter-drawer-footer">
          <button
            className="mobile-filter-apply-btn"
            onClick={() => setMobileFiltersOpen(false)}
          >
            Show {filteredProducts.length} Result
            {filteredProducts.length !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
